import React from 'react'
import { Virtuoso } from 'react-virtuoso'
import { Loader, Modal, SecondaryButton, TextBox } from 'components'

type Option = { text: string; value: string }
export type SelectBoxWithModalProps = {
  id: string
  placeholder: string
  label?: string
  buttonText: string
  buttonBelow?: boolean
  modalTitle: string
  disabled?: boolean
  isLoading?: boolean
  value: string | { value: string; text: string }
  onChange(value: string, text: string): void
  onOpen?: () => void
  options: Option[]
  error?: boolean
  helperText?: string
}

export const SelectBoxWithModal = (props: SelectBoxWithModalProps) => {
  const {
    id,
    placeholder,
    label,
    buttonText,
    buttonBelow,
    modalTitle,
    disabled,
    isLoading,
    value: innerValue,
    onChange,
    onOpen,
    options,
    error,
    helperText,
  } = props

  const { value, text } = React.useMemo(() => {
    return typeof innerValue === 'string' ? { value: innerValue, text: innerValue } : { ...innerValue }
  }, [innerValue])

  const [open, setOpen] = React.useState<boolean>(false)
  const [searchValue, setSearchValue] = React.useState<string>('')

  const filteredOptions = React.useMemo((): Option[] => {
    // Search text with non alphanumeric characters stripped
    const strippedSearchText = searchValue.toLowerCase().replace(/[^0-9a-z]/gi, '')

    return options.filter((option) => {
      return option.text
        .toLowerCase()
        .replace(/[^0-9a-z]/gi, '')
        .includes(strippedSearchText)
    })
  }, [options, searchValue])

  const handleChange = (value: string, text: string) => {
    onChange(value, text)
    handleClose()
  }

  const handleClose = () => {
    setSearchValue('')
    setOpen(false)
  }

  return (
    <>
      <div className="flex">
        <div className={`flex gap-2 w-full ${buttonBelow ? 'flex-col items-end sm:w-64' : 'flex-row sm:w-100'}`}>
          <TextBox
            id={id}
            label={label}
            placeholder={placeholder}
            outlined
            disabled={disabled}
            InputProps={{
              readOnly: true,
            }}
            value={text === 'all' ? 'All' : options?.find((item) => item.value === value)?.text ?? text}
            error={error}
            helperText={helperText}
          />
          <div className={`${buttonBelow ? '' : 'mt-11'}`}>
            <SecondaryButton
              buttonText={buttonText}
              minWidth="142px"
              disabled={disabled}
              onClick={() => {
                setOpen(true)
                onOpen && onOpen()
              }}
            />
          </div>
        </div>
      </div>
      {open && (
        <Modal title={modalTitle} open={open} onClose={handleClose}>
          {isLoading && <Loader variant="inlineFull" />}
          <div className="flex flex-col">
            <div className="w-full mb-8 sm:w-64">
              <TextBox id="search-box" placeholder="Search..." outlined value={searchValue} onChange={setSearchValue} />
            </div>
            <div className="w-full mb-10" style={{ height: 400 }}>
              {(!!filteredOptions.length && (
                <Virtuoso
                  allowFullScreen
                  totalCount={filteredOptions.length}
                  itemContent={(index: number) => {
                    const item = filteredOptions[index]

                    return (
                      <div
                        className={`p-3 border-b border-gray-200 cursor-pointer whitespace-pre-wrap hover:bg-primary-lighter ${
                          item.value === value ? 'bg-primary-lighter' : ''
                        }`}
                        key={item.value}
                        onClick={() => handleChange(item.value, item.text)}
                      >
                        <span className="whitespace-pre-wrap">{item.text}</span>
                      </div>
                    )
                  }}
                />
              )) || <span className="flex justify-center mt-32 text-lg font-semibold">No Data to Display</span>}
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
